import { useEffect, useRef, useState } from "react";
import { QrCode, X, CheckCircle2 } from "lucide-react";
import jsQR from "jsqr";

function beep(up) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = up ? 880 : 440;
    g.gain.setValueAtTime(0.25, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    o.start(); o.stop(ctx.currentTime + 0.35);
  } catch { /* no audio */ }
}

export const QrScanner = ({ title, hint, onScan, onClose, color }) => {
  const videoRef = useRef(null);
  const busyRef = useRef(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null); // {label, sub, tone: "green"|"amber"}

  useEffect(() => {
    let stream, raf, stopped = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const tick = () => {
      if (stopped) return;
      const v = videoRef.current;
      if (v && v.readyState === 4 && !busyRef.current) {
        canvas.width = v.videoWidth; canvas.height = v.videoHeight;
        ctx.drawImage(v, 0, 0);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const q = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
        if (q?.data) {
          busyRef.current = true;
          onScan(q.data).then((res) => {
            if (res) { setFlash(res); beep(res.tone !== "amber"); }
          }).finally(() => {
            setTimeout(() => { busyRef.current = false; setFlash(null); }, 2200);
          });
        }
      }
      raf = requestAnimationFrame(tick);
    };
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then((s) => {
        if (stopped) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        videoRef.current.srcObject = s;
        videoRef.current.play().catch(() => {});
        raf = requestAnimationFrame(tick);
      })
      .catch(() => setError("Camera unavailable — allow camera access and try again."));
    return () => { stopped = true; cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, [onScan]);

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${color ? "" : "bg-slate-950"}`} style={color ? { backgroundColor: color } : undefined} data-testid="badge-scanner">
      <div className="flex items-center justify-between px-5 py-4 text-white">
        <div className="flex items-center gap-2 font-display font-semibold">
          <QrCode className={`h-5 w-5 ${color ? "text-white" : "text-amber-400"}`} /> {title}
        </div>
        <button onClick={onClose} data-testid="scanner-close" className="h-10 w-10 rounded-md bg-red-600 text-white shadow-md shadow-red-600/30 inline-flex items-center justify-center hover:bg-red-700 transition-colors ml-4">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} muted playsInline className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-56 w-56 rounded-3xl border-4 border-white/70" />
        </div>
        {error && (
          <div className="absolute inset-x-6 top-6 rounded-xl bg-rose-600 text-white text-sm font-medium px-4 py-3 text-center" data-testid="scanner-error">{error}</div>
        )}
        {flash && (
          <div className={`absolute inset-0 flex flex-col items-center justify-center gap-2 ${
            flash.tone === "amber" ? "bg-amber-500/90" : "bg-emerald-600/90"
          }`} data-testid="scanner-flash">
            <CheckCircle2 className="h-16 w-16 text-white" />
            <div className="font-display text-3xl font-extrabold text-white text-center px-6">{flash.label}</div>
            <div className="text-lg font-semibold text-white/90 uppercase tracking-widest">{flash.sub}</div>
          </div>
        )}
      </div>
      <div className={`px-6 py-4 text-center text-xs ${color ? "text-white/80" : "text-slate-400"}`}>{hint}</div>
    </div>
  );
};
